import SenderSelect from '../components/SenderSelect';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import HtmlEmailEditor from '../components/HtmlEmailEditor';
import {
  ArrowLeft, Loader2, Sparkles, Send, Save, MailCheck, Users, Eye, CheckCircle2, Lock, Clock, CalendarClock, MousePointerClick,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CampaignBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [lists, setLists] = useState([]);
  const [senderIdentities, setSenderIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ name: '', from_name: '', sender_identity_id: '', sender_member_id: '', list_id: '', subject: '', html_body: '', topic: '' });

  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'later'
  const [scheduleAt, setScheduleAt] = useState('');
  const [clickers, setClickers] = useState([]);

  const readOnly = campaign && (campaign.status === 'sent' || campaign.status === 'sending');
  const isScheduled = campaign && campaign.status === 'scheduled';

  const load = useCallback(async () => {
    try {
      const [cRes, lRes, sRes] = await Promise.all([
        axios.get(`${API}/email-campaigns/${id}`),
        axios.get(`${API}/lists?object_type=contact`),
        axios.get(`${API}/sender-identities`),
      ]);
      setCampaign(cRes.data);
      setForm({
        name: cRes.data.name || '',
        from_name: cRes.data.from_name || '',
        sender_identity_id: cRes.data.sender_identity_id || '',
        sender_member_id: cRes.data.sender_member_id || '',
        list_id: cRes.data.list_id || '',
        subject: cRes.data.subject || '',
        html_body: cRes.data.html_body || '',
        topic: cRes.data.topic || '',
      });
      setLists(lRes.data.lists || []);
      setSenderIdentities(sRes.data.identities || []);
    } catch {
      toast.error('Campaign not found');
      navigate('/admin/campaigns');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (campaign && (campaign.clicked_count || 0) > 0) {
      axios.get(`${API}/email-campaigns/${id}/clicks`)
        .then((r) => setClickers(r.data.clickers || []))
        .catch(() => {});
    }
  }, [campaign, id]);
  useEffect(() => { if (user?.email && !testEmail) setTestEmail(user.email); }, [user, testEmail]);

  // Poll while sending so stats update live
  useEffect(() => {
    if (campaign?.status !== 'sending') return;
    const t = setInterval(async () => {
      const res = await axios.get(`${API}/email-campaigns/${id}`);
      setCampaign(res.data);
      if (res.data.status !== 'sending') clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  }, [campaign?.status, id]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/email-campaigns/${id}`, form);
      setCampaign(res.data);
      if (!silent) toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (aiPrompt.trim().length < 3) { toast.error('Describe the email you want first'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/email-campaigns/generate`, { prompt: aiPrompt.trim() });
      setForm((f) => ({ ...f, subject: res.data.subject || f.subject, html_body: res.data.html_body || f.html_body }));
      toast.success('Draft generated - review and tweak it below');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Enter an email'); return; }
    setTesting(true);
    try {
      await save(true);
      await axios.post(`${API}/email-campaigns/${id}/test`, { test_email: testEmail.trim(), base_url: window.location.origin });
      toast.success(`Test sent to ${testEmail}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test');
    } finally {
      setTesting(false);
    }
  };

  const openSend = async () => {
    const ok = await save(true);
    if (!ok) return;
    if (!form.list_id) { toast.error('Choose a recipient list first'); return; }
    if (!form.subject || !form.html_body) { toast.error('Add a subject and content first'); return; }
    setSendOpen(true);
    setPreviewCount(null);
    try {
      const res = await axios.get(`${API}/email-campaigns/${id}/preview-count`);
      setPreviewCount(res.data.count);
    } catch { setPreviewCount(0); }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      if (sendMode === 'later') {
        if (!scheduleAt) { toast.error('Pick a date and time'); setSending(false); return; }
        const res = await axios.post(`${API}/email-campaigns/${id}/schedule`, {
          scheduled_at: scheduleAt, timezone: 'Europe/London', base_url: window.location.origin,
        });
        toast.success('Campaign scheduled');
        setSendOpen(false);
        setCampaign((c) => ({ ...c, status: 'scheduled', scheduled_at: res.data.scheduled_at }));
      } else {
        const res = await axios.post(`${API}/email-campaigns/${id}/send`, { base_url: window.location.origin });
        toast.success(`Sending to ${res.data.total_recipients} recipients…`);
        setSendOpen(false);
        setCampaign((c) => ({ ...c, status: 'sending', total_recipients: res.data.total_recipients }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start sending');
    } finally {
      setSending(false);
    }
  };

  const cancelSchedule = async () => {
    try {
      await axios.post(`${API}/email-campaigns/${id}/unschedule`);
      toast.success('Schedule cancelled - back to draft');
      setCampaign((c) => ({ ...c, status: 'draft', scheduled_at: null }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;
  }

  const openRate = campaign?.sent_count ? Math.round((campaign.opened_count || 0) / campaign.sent_count * 100) : 0;
  const clickRate = campaign?.sent_count ? Math.round((campaign.clicked_count || 0) / campaign.sent_count * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="campaign-builder">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => navigate('/admin/campaigns')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="back-to-campaigns">
          <ArrowLeft className="w-4 h-4" /> Campaigns
        </button>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-campaign-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
              </Button>
              <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="open-test-btn">
                <MailCheck className="w-4 h-4 mr-2" /> Send test
              </Button>
              <Button onClick={openSend} className="bg-blue-600 hover:bg-blue-700 text-white shadow" data-testid="open-send-btn">
                <Send className="w-4 h-4 mr-2" /> Send to list
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Scheduled banner */}
      {isScheduled && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-center justify-between gap-4" data-testid="campaign-scheduled-banner">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-semibold text-gray-900">Scheduled to send</p>
              <p className="text-sm text-gray-600">
                {campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : ''} (Europe/London)
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={cancelSchedule} data-testid="cancel-schedule-btn">Cancel schedule</Button>
        </div>
      )}

      {/* Sent / sending banner */}
      {readOnly && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-testid="campaign-stats-banner">
          <div className="flex items-center gap-2 mb-4">
            {campaign.status === 'sending'
              ? <><Loader2 className="w-5 h-5 text-amber-500 animate-spin" /><span className="font-semibold text-gray-900">Sending in progress…</span></>
              : <><CheckCircle2 className="w-5 h-5 text-emerald-500" /><span className="font-semibold text-gray-900">Campaign sent</span></>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Recipients', value: campaign.total_recipients || 0, icon: Users },
              { label: 'Sent', value: campaign.sent_count || 0, icon: Send },
              { label: 'Opened', value: campaign.opened_count || 0, icon: Eye },
              { label: 'Open rate', value: `${openRate}%`, icon: Eye },
              { label: 'Clicked', value: campaign.clicked_count || 0, icon: MousePointerClick },
              { label: 'Click rate', value: `${clickRate}%`, icon: MousePointerClick },
            ].map((s) => (
              <div key={s.label} className="bg-gray-50 rounded-xl p-4">
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4 flex items-center gap-1"><Lock className="w-3 h-3" /> Sent campaigns are read-only. Duplicate from the list to resend.</p>
          {clickers.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-5" data-testid="who-clicked-list">
              <div className="flex items-center gap-2 mb-3">
                <MousePointerClick className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Who clicked <span className="text-gray-400 font-normal">({clickers.length})</span></h3>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {clickers.map((p, i) => (
                  <div key={p.email + i} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3" data-testid={`clicker-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{p.name || p.email}</div>
                        {p.name && <div className="text-xs text-gray-500 truncate">{p.email}</div>}
                      </div>
                      <div className="text-xs text-gray-400 shrink-0">
                        {p.clicked_at ? new Date(p.clicked_at).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : ''}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.links.map((l, j) => (
                        <a key={j} href={l.url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1 hover:bg-blue-100 transition-colors max-w-full"
                          title={l.url} data-testid={`clicker-${i}-link-${j}`}>
                          <MousePointerClick className="w-3 h-3 shrink-0" />
                          <span className="truncate max-w-[260px]">{l.url.replace(/^https?:\/\//, '')}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Builder form */}
      <fieldset disabled={readOnly} className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Campaign name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Send from</Label>
              <SenderSelect value={form.sender_member_id} testid="campaign-sender-select"
                onChange={(id, m) => setForm({ ...form, sender_member_id: id || '', from_name: m ? m.name : 'Care Tech Compare' })} />
            </div>
          </div>
          {senderIdentities.length > 0 && (
            <div className="space-y-2">
              <Label>Sender identity <span className="text-gray-400 font-normal">(the verified "from" address)</span></Label>
              <Select
                value={form.sender_identity_id || 'default'}
                onValueChange={(v) => {
                  if (v === 'default') { setForm({ ...form, sender_identity_id: '' }); return; }
                  const ident = senderIdentities.find((i) => i.id === v);
                  setForm({ ...form, sender_identity_id: v, from_name: ident?.from_name || form.from_name });
                }}
              >
                <SelectTrigger data-testid="campaign-sender-identity-select"><SelectValue placeholder="Default sender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default sender</SelectItem>
                  {senderIdentities.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.label} · {i.from_email}{i.is_default ? ' (default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Recipient list <span className="text-gray-400 font-normal">(contact lists only)</span></Label>
            {lists.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-500 bg-gray-50" data-testid="no-lists-hint">
                You don't have any contact lists yet.{' '}
                <button type="button" onClick={() => navigate('/admin/lists')} className="text-blue-600 hover:underline font-medium" data-testid="go-to-lists-link">
                  Create one in Lists
                </button>{' '}to choose recipients.
              </div>
            ) : (
              <Select value={form.list_id || undefined} onValueChange={(v) => setForm({ ...form, list_id: v })}>
                <SelectTrigger data-testid="campaign-list-select"><SelectValue placeholder="Choose a contact list…" /></SelectTrigger>
                <SelectContent>
                  {lists.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name} · {l.member_count} contacts</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>Preference stream <span className="text-gray-400 font-normal">(only opted-in contacts receive it)</span></Label>
            <Select value={form.topic || 'none'} onValueChange={(v) => setForm({ ...form, topic: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="campaign-topic-select"><SelectValue placeholder="No stream gate" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No stream gate (legacy - all subscribed)</SelectItem>
                <SelectItem value="one_to_one">One-to-one emails</SelectItem>
                <SelectItem value="marketing">Marketing &amp; offers</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="sector">Industry insights (sector)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Subject line</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="A subject your reader can't ignore…" data-testid="campaign-subject-input" />
          </div>
        </div>

        {/* AI draft */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-gray-900 text-sm">Draft with AI</span>
          </div>
          <Textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="e.g. Announce our new social media management package for care homes, friendly tone, 20% off the first 3 months, CTA to book a call."
            className="bg-white border-blue-200 resize-none h-20"
            data-testid="ai-prompt-input"
          />
          <div className="flex justify-end mt-3">
            <Button onClick={generate} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-ai-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate draft</>}
            </Button>
          </div>
        </div>

        {/* Body editor */}
        <div className="space-y-2">
          <Label>Email content</Label>
          <HtmlEmailEditor value={form.html_body} onChange={(html) => setForm((f) => ({ ...f, html_body: html }))} disabled={readOnly} />
          <p className="text-xs text-gray-400">An unsubscribe link is added automatically to every email.</p>
        </div>
      </fieldset>

      {/* Test dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-white" data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MailCheck className="w-5 h-5 text-blue-600" /> Send a test</DialogTitle>
            <DialogDescription>We'll send this exact email (with sample personalization) to one address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Send test to</Label>
            <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={testing} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-test-btn">
              {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send confirm dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-white" data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Send campaign</DialogTitle>
            <DialogDescription>
              {previewCount === null
                ? 'Checking recipients…'
                : <>This will email <b>{previewCount}</b> contact{previewCount === 1 ? '' : 's'} in <b>{lists.find((l) => l.id === form.list_id)?.name}</b>. Unsubscribed contacts are skipped automatically.</>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button" onClick={() => setSendMode('now')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${sendMode === 'now' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                data-testid="send-mode-now">
                <Send className="w-4 h-4" /> Send now
              </button>
              <button type="button" onClick={() => setSendMode('later')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${sendMode === 'later' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                data-testid="send-mode-later">
                <CalendarClock className="w-4 h-4" /> Schedule
              </button>
            </div>
            {sendMode === 'later' && (
              <div className="space-y-1.5">
                <Label>Send at (Europe/London)</Label>
                <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="schedule-datetime-input" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={confirmSend} disabled={sending || previewCount === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-send-btn">
              {sending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {sendMode === 'later' ? 'Scheduling…' : 'Starting…'}</>
                : (sendMode === 'later' ? 'Schedule send' : <>Send to {previewCount ?? '…'} now</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignBuilder;
